import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './EstimateList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { ITemplate } from '~/utils/interface/template';
import usePermission from '~/hooks/usePermission';
import dayjs from 'dayjs';
import MenuOption from '../menu-option/MenuOption';
import { estimateTemplateActions } from '~/thunks/estimate-template/estimateTemplateSlice';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type EstimateTemplateProps = {
  onOpenEdit: (id: string) => void;
};

function EstimateTemplateList(props: EstimateTemplateProps) {
  const { t } = useTranslation('admin/template');
  const headCells: Array<HeadCelType> = [
    {
      id: 'name',
      label: t(`properties.name`),
      sortable: true,
      searchable: true,
    },

    {
      id: 'createdAt',
      label: t(`properties.createdAt`),
      sortable: true,
      searchable: true,
    },
    {
      id: '',
      label: '',
      sortable: false,
      searchable: false,
    },
  ];
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { estimateTemplates, currentPage, totalPages, filter } = useAppSelector((state) => state.estimateTemplate);

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(estimateTemplateActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(estimateTemplateActions.changeMenuSelectId(menuSelectedId));
      dispatch(estimateTemplateActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      estimateTemplateActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      estimateTemplateActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('template-email-list')}>
      <BaseTable<ITemplate>
        data={estimateTemplates}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(item: ITemplate) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('name')}>{item.name}</Box>
              </TableCell>

              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('createdAt')}>
                  {item?.createdAt ? dayjs(item.createdAt).format('DD MMM, YYYY') : ''}
                </Box>
              </TableCell>

              {(permissions[PERMISSIONS.estimateTemplate_edit] || permissions[PERMISSIONS.estimateTemplate_delete]) && (
                <TableCell>
                  <Box className={cx('action-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, item.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.estimateTemplate_edit]}
        enableDelete={permissions[PERMISSIONS.estimateTemplate_delete]}
      />
    </Box>
  );
}

export default memo(EstimateTemplateList);
